import _ from 'lodash';
import { Badge } from 'reactstrap';
import {
  ConfirmModal,
  IconButton,
  WhiteDeleteIcon as whiteDeleteIcon,
  WhiteEyeIcon as whiteEyeIcon,
} from '@/shared/ui';
import RhythmEventPreviewStrip from '@/features/event-strip-preview';
import { eventOffsetPreviewStrip } from '@/entities/strip';
import useNewEvents from './useNewEvents';

export type NewEventsProps = {
  onCancel: () => void;
  ecgDataMap: any;
  timezoneOffset: number;
  startReport: any;
};

/**
 * Renders the New events modal listing draft rhythm-event strips awaiting confirmation, each with remove and view overlay actions. Reads the draft list and thumbnail display settings from useNewEvents, renders one preview strip per draft behind a red count badge, and gates removal behind a confirmation modal keyed by stripRemoveIndex.
 *
 * @param props - ECG data map, timezone offset and report start used to draw the draft strips.
 * @returns The new-events modal body with its draft strip list, remove confirmation and Close footer.
 */
const NewEvents = ({ onCancel, ecgDataMap, timezoneOffset, startReport }: NewEventsProps) => {
  const {
    keyRecoil,
    eventNews,
    selectedStrip,
    activeButton,
    channelsThumbnail,
    highPassThumbnail,
    gainThumbnail,
    isOpenConfirmRemove,
    setIsOpenConfirmRemove,
    stripRemoveIndex,
    onClickCancel,
    onClickRemoveStrips,
    onClickConfirmRemoveStrip,
    handleClickSelectEvent,
  } = useNewEvents({ onCancel });

  return (
    <>
      <ConfirmModal
        className="delete-instant-report-modal"
        isOpen={isOpenConfirmRemove}
        onClickLeftButton={() => {
          stripRemoveIndex.current = undefined;
          setIsOpenConfirmRemove(false);
        }}
        onClickRightButton={onClickConfirmRemoveStrip}
        leftButtonName="Cancel"
        rightButtonName="Remove"
        isRedBtn
        title="Remove event"
        question="This action cannot be undone. Are you sure you want to remove this event?"
      />
      <div className="draggable-modal-content new-events">
        <div className="content-title">
          New events
          {eventNews.length > 0 && <Badge className="custom-badge-red">{eventNews.length}</Badge>}
        </div>
        <div className="content">
          {eventNews.length > 0 ? (
            _.map(eventNews, (holterEvent, index) => (
              <RhythmEventPreviewStrip
                id={`rh-new-event-${holterEvent.id}`}
                index={index}
                onClickSelectEvent={handleClickSelectEvent}
                keyRecoil={keyRecoil}
                activeButton={activeButton}
                overlayContent={
                  <div className="overlay-single-strip">
                    <IconButton
                      id={`rh-event-remove-draft-strip-${index}`}
                      isOutline
                      className="overlay-strip-btn"
                      iconComponent={<img src={whiteDeleteIcon} alt="Delete icon" />}
                      onClick={() => {
                        onClickRemoveStrips(index);
                      }}
                    />
                    <IconButton
                      id={`rh-event-view-draft-strip-${index}`}
                      className="overlay-strip-btn ml3"
                      isOutline
                      iconComponent={<img src={whiteEyeIcon} alt="View icon" />}
                      onClick={() => {
                        handleClickSelectEvent(holterEvent);
                      }}
                    />
                  </div>
                }
                event={holterEvent}
                isActive={holterEvent.id === selectedStrip?.id}
                ecgDataMap={ecgDataMap}
                width={296}
                height={67.3}
                highPass={highPassThumbnail}
                gain={gainThumbnail}
                channels={channelsThumbnail}
                customOffset={eventOffsetPreviewStrip}
                timezoneOffset={timezoneOffset}
                startReport={startReport}
              />
            ))
          ) : (
            <div className="no-event">There is no event</div>
          )}
        </div>
      </div>
      <div className="footer-btn">
        <button
          id="rh-event-close-new-events"
          className="draggable-modal-btn single-btn"
          onClick={onClickCancel}
        >
          Close
        </button>
      </div>
    </>
  );
};

export default NewEvents;
